import { useEffect, useState, useCallback, useRef } from 'react';
import { imagePreloader, ImagePreloadConfig, PreloadResult, ImagePreloaderService } from '../services/ImagePreloader';

export interface UseImagePreloaderResult {
  preloadImages: (images: any[], config: ImagePreloadConfig) => Promise<PreloadResult[]>;
  preloadColorImages: (images: any[], colors: string[]) => Promise<void>;
  isPreloading: boolean;
  statistics: ReturnType<typeof imagePreloader.getStatistics>;
  clearCache: () => void;
}

export const useImagePreloader = (): UseImagePreloaderResult => {
  const [isPreloading, setIsPreloading] = useState(false);
  const [statistics, setStatistics] = useState(imagePreloader.getStatistics());
  const updateStatsInterval = useRef<NodeJS.Timeout>();
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // Update statistics periodically during preloading
  useEffect(() => {
    if (isPreloading) {
      updateStatsInterval.current = setInterval(() => {
        if (isMounted.current) setStatistics(imagePreloader.getStatistics());
      }, 1000);
    } else if (updateStatsInterval.current) {
      clearInterval(updateStatsInterval.current);
    }

    return () => {
      if (updateStatsInterval.current) {
        clearInterval(updateStatsInterval.current);
      }
    };
  }, [isPreloading]);

  const preloadImages = useCallback(async (
    images: any[], 
    config: ImagePreloadConfig
  ): Promise<PreloadResult[]> => {
    if (isMounted.current) setIsPreloading(true);
    try {
      const urls = imagePreloader.generateImageUrls(images, config);
      const results = await imagePreloader.preloadImages(urls, config);
      if (isMounted.current) setStatistics(imagePreloader.getStatistics());
      return results;
    } finally {
      if (isMounted.current) setIsPreloading(false);
    }
  }, []);

  const preloadColorImages = useCallback(async (
    images: any[], 
    colors: string[]
  ): Promise<void> => {
    if (isMounted.current) setIsPreloading(true);
    try {
      // Tier 1: Critical images for default color (Black)
      await preloadImages(images, {
        ...ImagePreloaderService.PRIORITY_CONFIGS.CRITICAL,
        colors: ['black']
      });

      // Tier 2: Thumbnail images for all colors (high priority)
      await preloadImages(images, {
        ...ImagePreloaderService.PRIORITY_CONFIGS.HIGH,
        colors
      });

      // Tier 3: Display images for all colors (medium priority)
      preloadImages(images, {
        ...ImagePreloaderService.PRIORITY_CONFIGS.MEDIUM,
        colors
      }).catch(console.warn); // Don't block on medium priority

      // Preload color option thumbnails
      //imagePreloader.preloadColorOptions(colors).catch(console.warn);

    } finally {
      if (isMounted.current) setIsPreloading(false);
    }
  }, [preloadImages]);

  const clearCache = useCallback(() => {
    imagePreloader.clearCache();
    if (isMounted.current) setStatistics(imagePreloader.getStatistics());
  }, []);

  return {
    preloadImages,
    preloadColorImages,
    isPreloading,
    statistics,
    clearCache,
  };
};

// Hook for adaptive preloading based on network conditions
export const useAdaptiveImagePreloader = () => {
  const [networkSpeed, setNetworkSpeed] = useState<'slow' | 'fast'>('fast');
  const baseHook = useImagePreloader();

  useEffect(() => {
    // Detect network speed using Navigator API
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    
    if (connection) {
      const updateNetworkSpeed = () => {
        const effectiveType = connection.effectiveType;
        setNetworkSpeed(['slow-2g', '2g', '3g'].includes(effectiveType) ? 'slow' : 'fast');
      };

      updateNetworkSpeed();
      connection.addEventListener('change', updateNetworkSpeed);

      return () => connection.removeEventListener('change', updateNetworkSpeed);
    }
  }, []);

  const adaptivePreloadColorImages = useCallback(async (
    images: any[], 
    colors: string[]
  ): Promise<void> => {
    if (networkSpeed === 'slow') {
      // On slow networks, only preload critical images
      await baseHook.preloadImages(images, {
        ...ImagePreloaderService.PRIORITY_CONFIGS.CRITICAL,
        colors: ['black'],
        maxConcurrent: 2, // Reduce concurrent requests
      });
    } else {
      // On fast networks, use full preloading
      await baseHook.preloadColorImages(images, colors);
    }
  }, [baseHook, networkSpeed]);

  return {
    ...baseHook,
    preloadColorImages: adaptivePreloadColorImages,
    networkSpeed,
  };
};